import {
  type CountryCode,
  parsePhoneNumberFromString,
  type PhoneNumber
} from 'libphonenumber-js';
import validator from 'validator';
import type { ContactDetailType } from 'v5/core/entity-schema/entitySchema.types';
import type { SocialProfile } from './social/abstractLinkResolver';
import { type Platform, socialResolvers } from './social/socialResolvers';

/**
 * One contact detail as the row editor sees it. `value` is what the person
 * typed and is saved as it is; everything below only reads it.
 */
export type ContactDetailRowValue = {
  value: string;
  /** Work / Personal … for emails, phones and websites; the platform for socials. */
  type: string;
  /** Phones: the country a national number is read in (`value_3`). */
  country: CountryCode | null;
};

/** Help shown under a row. It never blocks Save: a phone is saved as typed. */
export type ContactDetailHint =
  | { code: 'phone-unreadable' }
  /** Too short or long for the numbers of its calling code. */
  | { code: 'phone-incomplete'; callingCode: string }
  /** A national trunk 0 kept after the country code: “+44 07891…”. */
  | { code: 'phone-trunk-zero'; fix: string }
  /** Same as an earlier row (index), compared the way the type reads. */
  | { code: 'duplicate'; ofRow: number };

/** A problem that blocks Save; `part` is the control to move to. */
export type ContactDetailError =
  | { code: 'platform-required'; part: 'type' }
  | { code: 'not-an-email'; part: 'value' }
  | { code: 'not-a-website'; part: 'value' };

/** What a row shows at rest. */
export type ContactDetailDisplay = {
  text: string;
  /** Where the value leads (`tel:`, `mailto:`, the profile, the site); `null` when nothing sensible. */
  href: string | null;
  /** Phones: the country the number belongs to, for its flag. */
  country: CountryCode | null;
  /** Socials: the platform the value resolves to, and a named profile (LinkedIn). */
  platform: Platform | null;
  profile: SocialProfile | null;
};

interface ContactDetailKind {
  hint(row: ContactDetailRowValue): ContactDetailHint | null;
  error(row: ContactDetailRowValue): ContactDetailError | null;
  /** Rows with the same key are duplicates; `null` never matches. */
  duplicateKey(row: ContactDetailRowValue): string | null;
  display(row: ContactDetailRowValue): ContactDetailDisplay;
  /** Whether a paste holding several values offers to split into rows. */
  splitsPaste: boolean;
}

function isBlank(row: ContactDetailRowValue): boolean {
  return row.value.trim() === '';
}

function parsePhone(row: ContactDetailRowValue): PhoneNumber | undefined {
  return parsePhoneNumberFromString(row.value.trim(), row.country ?? undefined);
}

/**
 * “+44 07891 546888”: a national trunk 0 typed after the country code. The
 * parser reads past it, so the slip shows only in the text; the fix is the
 * number written without it.
 */
function trunkZeroFix(text: string, phone: PhoneNumber): string | null {
  const digits = text.replace(/\D/g, '');
  // A national number that keeps its 0 (Italy: +39 06…) is no slip.
  return text.trim().startsWith('+') &&
    digits.startsWith(`${phone.countryCallingCode}0`) &&
    !phone.nationalNumber.startsWith('0') &&
    phone.isValid()
    ? phone.formatInternational()
    : null;
}

function isPlatform(type: string): type is Platform {
  return type in socialResolvers;
}

function toPlatform(type: string): Platform {
  const normalized = type.toLowerCase().trim();
  return isPlatform(normalized) ? normalized : 'other';
}

function withScheme(value: string): string {
  return /^https?:\/\//i.test(value) ? value : `https://${value}`;
}

const plainDisplay = (
  text: string,
  href: string | null
): ContactDetailDisplay => ({
  text,
  href,
  country: null,
  platform: null,
  profile: null
});

export const contactDetailKinds: Record<ContactDetailType, ContactDetailKind> =
  {
    EMAIL: {
      hint: () => null,
      error: row =>
        isBlank(row) || validator.isEmail(row.value.trim())
          ? null
          : { code: 'not-an-email', part: 'value' },
      duplicateKey: row =>
        isBlank(row) ? null : row.value.trim().toLowerCase(),
      display: row => plainDisplay(row.value, `mailto:${row.value.trim()}`),
      splitsPaste: true
    },
    PHONE: {
      hint: row => {
        if (isBlank(row)) return null;
        const phone = parsePhone(row);
        if (!phone) return { code: 'phone-unreadable' };
        const fix = trunkZeroFix(row.value, phone);
        if (fix !== null) return { code: 'phone-trunk-zero', fix };
        return phone.isValid()
          ? null
          : {
              code: 'phone-incomplete',
              callingCode: String(phone.countryCallingCode)
            };
      },
      error: () => null,
      duplicateKey: row => {
        if (isBlank(row)) return null;
        return parsePhone(row)?.number ?? row.value.replace(/[^\d+]/g, '');
      },
      display: row => {
        const phone = isBlank(row) ? undefined : parsePhone(row);
        const dialable = phone?.number ?? row.value.replace(/[^\d+]/g, '');
        // A national number is in the row's country, whatever range the parser files it under.
        const international = row.value.trim().startsWith('+');
        return {
          ...plainDisplay(row.value, dialable ? `tel:${dialable}` : null),
          country: international ? (phone?.country ?? null) : row.country
        };
      },
      splitsPaste: true
    },
    SOCIAL: {
      hint: () => null,
      error: row =>
        !isBlank(row) && row.type.trim() === ''
          ? { code: 'platform-required', part: 'type' }
          : null,
      duplicateKey: row => {
        if (isBlank(row)) return null;
        const platform = toPlatform(row.type);
        const handle =
          socialResolvers[platform].parseInput(row.value.trim())?.handle ??
          row.value.trim();
        return `${platform}:${handle.toLowerCase()}`;
      },
      display: row => {
        const platform = toPlatform(row.type);
        const parsed = isBlank(row)
          ? null
          : socialResolvers[platform].parseInput(row.value.trim());
        return {
          text: parsed?.handle || row.value,
          href: parsed?.fullUrl || null,
          country: null,
          platform,
          profile: parsed?.profile ?? null
        };
      },
      splitsPaste: false
    },
    WEBSITE: {
      hint: () => null,
      error: row =>
        isBlank(row) || validator.isURL(row.value.trim())
          ? null
          : { code: 'not-a-website', part: 'value' },
      duplicateKey: row =>
        isBlank(row)
          ? null
          : row.value
              .trim()
              .toLowerCase()
              .replace(/^https?:\/\//, '')
              .replace(/^www\./, '')
              .replace(/\/$/, ''),
      display: row =>
        plainDisplay(
          row.value,
          isBlank(row) ? null : withScheme(row.value.trim())
        ),
      // Links carry commas (map links): a paste is one site.
      splitsPaste: false
    }
  };

/**
 * Each row's hint: a duplicate of an earlier row first, else the type's own
 * hint; `null` for a row with nothing to say.
 */
export function contactDetailHints(
  type: ContactDetailType,
  rows: ReadonlyArray<ContactDetailRowValue>
): Array<ContactDetailHint | null> {
  const kind = contactDetailKinds[type];
  const firstRowByKey = new Map<string, number>();
  return rows.map((row, index) => {
    const key = kind.duplicateKey(row);
    if (key !== null) {
      const first = firstRowByKey.get(key);
      if (first !== undefined) return { code: 'duplicate', ofRow: first };
      firstRowByKey.set(key, index);
    }
    return kind.hint(row);
  });
}

/**
 * The phone widget’s paste rule, the only place a value is reformatted: 5+
 * digits; “+…” stays as pasted (`null`); a national or “00” number the row's
 * country reads becomes its international form; anything else stays as pasted.
 */
export function formatPastedPhone(
  text: string,
  country: CountryCode | null
): string | null {
  const digits = text.replace(/\D/g, '');
  if (digits.length < 5 || text.trim().startsWith('+') || country === null) {
    return null;
  }
  const phone = parsePhoneNumberFromString(text, country);
  return phone?.isValid() ? phone.number : null;
}

/** The values a paste holds when it lists several (new lines, commas, semicolons). */
export function splitPastedValues(text: string): string[] {
  return text
    .split(/[\n;,]+/)
    .map(part => part.trim())
    .filter(part => part !== '');
}

/** A new row’s type: the first option no row uses yet; socials start without a platform. */
export function nextContactDetailType(
  type: ContactDetailType,
  options: ReadonlyArray<string>,
  used: ReadonlyArray<string>
): string {
  if (type === 'SOCIAL') return '';
  return options.find(option => !used.includes(option)) ?? options[0] ?? '';
}

/** The platforms that read `value` (score ≥ 0.5). */
function readersOf(value: string): Array<{ key: Platform; score: number }> {
  const text = value.trim();
  // Scores: 1 a link, 0.9 a prefix, 0.5–0.85 a handle shape, 0.3 any text.
  return (Object.keys(socialResolvers) as Platform[])
    .filter(key => socialResolvers[key].parseInput(text) !== null)
    .map(key => ({ key, score: socialResolvers[key].getMatchingScore(text) }))
    .filter(reader => reader.score >= 0.5);
}

/** The one platform that reads `value`, if only one does. */
function soleReader(value: string): { key: Platform; score: number } | null {
  const readers = readersOf(value);
  return readers.length === 1 ? readers[0] : null;
}

/**
 * The platform a social row has after its text changed: a link
 * (“instagram.com/jane”) or a prefix (“ig:jane”) one platform reads moves it,
 * over a chosen one. A handle's shape changes with every letter (“jane-” fits
 * LinkedIn alone, “jane-doe” six platforms), so it waits for `platformWhenLeft`.
 */
export function platformAfterEdit(value: string, platform: string): string {
  const reader = soleReader(value);
  return reader !== null && reader.score >= 0.9 ? reader.key : platform;
}

/**
 * The platform a social row has once it was left: an empty one is filled from
 * a handle only one platform's names fit (“jane#1234”), and a link no listed
 * platform reads is Other (as legacy); a chosen one stays.
 */
export function platformWhenLeft(value: string, platform: string): string {
  if (platform.trim() !== '') return platform;
  const readers = readersOf(value);
  if (readers.length === 1) return readers[0].key;
  return readers.length === 0 && validator.isURL(value.trim())
    ? 'other'
    : platform;
}

/**
 * The country a phone row stores (`value_3`) after its text changed: an
 * international number's own, else the one the row reads it in. The text is
 * never touched.
 */
export function phoneCountryAfterEdit(
  value: string,
  country: CountryCode | null
): CountryCode | null {
  if (!value.trim().startsWith('+')) return country;
  return parsePhoneNumberFromString(value.trim())?.country ?? country;
}
